// src/pages/actividades/components/agenda/ActivityBlock.tsx
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { ActividadListItem, AreaActividad } from '@/types';

interface Props {
  activity: ActividadListItem;
  top: number;
  height: number;
  column: number;
  totalColumns: number;
  style?: React.CSSProperties;
}

const AREA_STYLES: Record<AreaActividad | 'default', { bg: string; border: string; bgActive: string; text: string }> = {
  TRABAJO: { bg: 'bg-blue-50', border: 'border-blue-500', bgActive: 'bg-blue-100', text: 'text-blue-900' },
  ADMINISTRATIVO: { bg: 'bg-amber-50', border: 'border-amber-500', bgActive: 'bg-amber-100', text: 'text-amber-900' },
  PERSONAL: { bg: 'bg-emerald-50', border: 'border-emerald-500', bgActive: 'bg-emerald-100', text: 'text-emerald-900' },
  default: { bg: 'bg-gray-50', border: 'border-gray-400', bgActive: 'bg-gray-100', text: 'text-gray-700' },
};

const PRIORITY_ICONS: Record<string, string> = {
  AAA: '\uD83D\uDD34',
  AA: '\uD83D\uDFE0',
};

function getSubtitle(activity: ActividadListItem): string {
  if (activity.cliente_nombre) return activity.cliente_nombre;
  if (activity.tipo_actividad?.nombre) return activity.tipo_actividad.nombre;
  return activity.area;
}

function formatTimeRange(hora: string, duracion?: number): string {
  if (!duracion) return hora.slice(0, 5);
  const [h, m] = hora.split(':').map(Number);
  const endTotal = h * 60 + (m || 0) + duracion;
  const endH = Math.floor(endTotal / 60) % 24;
  const endM = endTotal % 60;
  return `${hora.slice(0, 5)} – ${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}

export function ActivityBlock({ activity, top, height, column, totalColumns, style }: Props) {
  const navigate = useNavigate();
  const isCompleted = activity.estado === 'COMPLETADA' || activity.estado === 'CANCELADA';
  const isInProgress = activity.estado === 'EN_PROCESO';
  const areaKey = (AREA_STYLES[activity.area] ? activity.area : 'default') as AreaActividad | 'default';
  const colors = AREA_STYLES[areaKey];

  const widthPct = 100 / totalColumns;
  const leftPct = column * widthPct;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/actividades/${activity.id}`);
  };

  // Responsive content based on pixel height
  const isSmall = height < 50;
  const isMedium = height >= 50 && height <= 100;
  // isLarge = height > 100

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'absolute rounded-md border-l-4 px-2 py-1 text-left overflow-hidden cursor-pointer transition-shadow',
        'hover:shadow-md hover:z-10',
        colors.border,
        isInProgress ? colors.bgActive : colors.bg,
        isCompleted && 'opacity-50',
        colors.text
      )}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        paddingRight: totalColumns > 1 ? '2px' : undefined,
        ...style,
      }}
      title={activity.titulo}
    >
      <div className="flex items-center gap-1 min-w-0">
        {isInProgress && (
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
          </span>
        )}
        <span className={cn(
          'text-xs font-medium truncate',
          isCompleted && 'line-through'
        )}>
          {activity.titulo}
        </span>
      </div>

      {!isSmall && (
        <div className="text-[10px] opacity-70 truncate mt-0.5">
          {getSubtitle(activity)}
        </div>
      )}

      {!isSmall && !isMedium && activity.hora_agendada && (
        <>
          <div className="text-[10px] opacity-60 mt-0.5">
            {formatTimeRange(activity.hora_agendada, activity.duracion_estimada_min)}
          </div>
          {PRIORITY_ICONS[activity.prioridad] && (
            <div className="text-xs mt-0.5">
              {PRIORITY_ICONS[activity.prioridad]}
            </div>
          )}
        </>
      )}
    </button>
  );
}
